import { TRANSACTION_ITEM_TYPES, CHARGE_TYPE } from './constants';
import { formatCurrency } from './utils';

interface TransactionItem {
  type: string;
  itemId?: string;
  value?: string;
}

interface OrderItem {
  id: string;
  product: { name: string };
  color?: string;
  size?: string;
}

interface AdditionalCharge {
  _id: { toString(): string };
  name: string;
}

interface Order {
  items: OrderItem[];
  cancelledItems?: OrderItem[];
  additionalCharges: AdditionalCharge[];
}

interface Transaction {
  chargeType?: string;
  chargeValue?: number;
}

/**
 * Formats transaction items into a readable list.
 */
export function formatTransactionItems(
  transactionItems: TransactionItem[] | undefined,
  order: Order,
): string[] {
  if (!transactionItems || transactionItems.length === 0) {
    return [];
  }

  return transactionItems
    .map((transItem): string | null => {
      if (transItem.type === TRANSACTION_ITEM_TYPES.ORDER_ITEM) {
        // cancelled items keep their label in the transaction history
        const item = [...order.items, ...(order.cancelledItems ?? [])]
          .find((itm) => itm.id === transItem.itemId);
        if (item) {
          return `${item.product.name} (${item.color || 'N/A'}/${item.size || 'N/A'})`;
        }
      } else if (transItem.type === TRANSACTION_ITEM_TYPES.ADDITIONAL_CHARGE) {
        const charge = order.additionalCharges.find(
          (ac) => ac._id.toString() === transItem.value,
        );
        if (charge) {
          return `${charge.name} (Additional Charge)`;
        }
      }
      return null;
    })
    .filter((entry): entry is string => Boolean(entry));
}

/**
 * Formats charge type information for display
 * (e.g. " (30%)" or " (Fixed: $500.00)").
 */
export function formatChargeInfo(transaction: Transaction): string {
  if (!transaction.chargeType || !transaction.chargeValue) {
    return '';
  }

  if (transaction.chargeType === CHARGE_TYPE.PERCENTAGE) {
    return ` (${transaction.chargeValue}%)`;
  } else if (transaction.chargeType === CHARGE_TYPE.FIXED) {
    return ` (Fixed: ${formatCurrency(transaction.chargeValue)})`;
  }

  return '';
}
